
import { ref, Ref, defineComponent, watch, computed, getCurrentInstance } from 'vue';
import * as Cesium from 'cesium';
import { useStore } from 'vuex';
import {
  useTrack,
  FunctionIdEnum,
  EventTypeEnum,
} from '@/components/sendMetrics';

const { track } = useTrack();
const VUE_APP_IS_DDE = process.env.VUE_APP_IS_DDE == 'true';

export default defineComponent({
  props: {
    viewer: {
      type: Cesium.Viewer,
    },
  },

  setup(props) {
    const instance = getCurrentInstance();
    const eb = instance?.appContext.config.globalProperties.$EventBus;
    const store = useStore();
    const state = store.state;

    eb.on('timeline.remove',()=>{
      toggleBaselayer(baseLayerList[0]);
    });

    //二三维控制
    let current23D: Ref<2 | 3> = ref(2);
    let toggle23D = () => {
      if (state.timelineStore.visible) {
        store.commit('timelineStore/setVisible', false);
        eb.emit('timeline.remove');
        store.commit('timelineStore/reset');
      }

      if (props.viewer?.scene.mode === Cesium.SceneMode.SCENE3D) {
        props.viewer?.scene.morphTo2D();
        current23D.value = 2;
      } else {
        props.viewer?.scene.morphTo3D();
        current23D.value = 3;
      }
    };

    //地形控制
    let demUrl = '//data.mars3d.cn/terrain';
    let demChecked = ref(false);
    watch(demChecked, (v) => {
      if (v) {
        (props.viewer as Cesium.Viewer).scene.globe.terrainExaggeration = 1.0;
        (props.viewer as Cesium.Viewer).scene.terrainProvider =
          new Cesium.CesiumTerrainProvider({
            url: demUrl,
            requestWaterMask: true,
          });
      } else {
        (props.viewer as Cesium.Viewer).scene.terrainProvider =
          new Cesium.EllipsoidTerrainProvider({});
      }
    });
    
    //底图控制
    type BaseLayer = {
      url: string;
      subdomains: string;
      id: string;
      name: string;
      thumbnail?: string;
      annotion?: BaseLayer;
    };
    const baseLayerList: BaseLayer[] = [
      {
        id: 'yingxiang',
        name: '影像图',
        url: process.env.VUE_APP_BASEMAP_YINGXIANG,
        subdomains: '123',
        thumbnail: `./images/yingxiang${VUE_APP_IS_DDE ? '-dde' : ''}.png`,
        annotion: {
          id: 'yingxiang_annotion',
          name: '影像注记',
          url: process.env.VUE_APP_BASEMAP_YINGXIANG_CIA,
          subdomains: '123',
        },
      },
      {
        id: 'xianhua',
        name: '线划图',
        url: process.env.VUE_APP_BASEMAP_XIANHUA,
        subdomains: '123',
        thumbnail: `./images/xianhua${VUE_APP_IS_DDE ? '-dde' : ''}.png`,
      },
      {
        id: 'dishi',
        name: '地势图',
        thumbnail: `./images/dishi${VUE_APP_IS_DDE ? '-dde' : ''}.png`,
        url: process.env.VUE_APP_BASEMAP_DISHI,
        subdomains: '123',
      },
    ];
    const currentBaseLayerID = ref('yingxiang');
    const currentBaseLayer = computed(() => {
      return (
        baseLayerList.find((o) => o.id == currentBaseLayerID.value) || null
      );
    });
    const createDefaultBaselayer = () => {
      return new Cesium.UrlTemplateImageryProvider({
        url: process.env.VUE_APP_BASEMAP_YINGXIANG,
        subdomains: ['1', '2', '3'],
        maximumLevel: 18,
      });
    };
    const toggleBaselayer = (layer: BaseLayer) => {
      let viewer = props.viewer;
      viewer?.imageryLayers.remove(viewer.imageryLayers.get(0));

      if (layer.id === currentBaseLayerID.value) {
        currentBaseLayerID.value = '';
        viewer?.imageryLayers.addImageryProvider(createDefaultBaselayer(), 0);
      } else {
        currentBaseLayerID.value = layer.id;
        viewer?.imageryLayers.addImageryProvider(
          new Cesium.UrlTemplateImageryProvider(layer),
          0,
        );
      }

      track(
        FunctionIdEnum.globalLayer_earth_changeBaseMap,
        EventTypeEnum.CLICK,
        {
          basemap: layer.name,
        },
      );
    };

    //注记图层
    let annotionChecked = ref(false);
    let annotionLayer: Cesium.ImageryLayer | null | undefined = null;
    let addAnnotionLayer = () => {
      removeAnnotionLayer();

      let { imageryLayers } = props.viewer as Cesium.Viewer;

      let imProvider = new Cesium.UrlTemplateImageryProvider(
        currentBaseLayer.value?.annotion as BaseLayer,
      );
      (imProvider as any).isAnnotion = true;
      imageryLayers.addImageryProvider(imProvider);

      annotionLayer = imageryLayers.get(imageryLayers.length - 1);
    };
    let removeAnnotionLayer = () => {
      let { imageryLayers } = props.viewer as Cesium.Viewer;
      imageryLayers.contains(annotionLayer as Cesium.ImageryLayer) &&
        imageryLayers.remove(annotionLayer as Cesium.ImageryLayer);
    };
    watch(annotionChecked, (v) => {
      if (v) {
        addAnnotionLayer();
      } else {
        removeAnnotionLayer();
      }
    });
    watch(currentBaseLayerID, (v) => {
      if (v === '') {
        annotionChecked.value = false;
      } else {
        if (currentBaseLayer.value?.annotion) {
          annotionChecked.value && addAnnotionLayer();
        } else {
          removeAnnotionLayer();
        }
      }
    });
    var flag = true;
    watch(
      () => props.viewer,
      () => {
        if (flag && props?.viewer?.imageryLayers) {
          flag = false;

          props.viewer.imageryLayers.layerAdded.addEventListener(
            (layer: Cesium.ImageryLayer) => {
              //刚被添加的图层不是注记层，并且，当前用户已经叠加过注记层，那么，要保证注记层是最上边的
              if (
                !(layer.imageryProvider as any).isAnnotion &&
                annotionChecked.value
              ) {
                props?.viewer?.imageryLayers.contains(
                  annotionLayer as Cesium.ImageryLayer,
                ) &&
                  props?.viewer?.imageryLayers.raiseToTop(
                    annotionLayer as Cesium.ImageryLayer,
                  );
              }
            },
            props?.viewer?.imageryLayers,
          );
        }
      },
    );

    return {
      toggle23D,
      currentBaseLayerID,
      baseLayerList,
      toggleBaselayer,
      current23D,
      demChecked,
      currentBaseLayer,
      annotionChecked,
    };
  },
});
